import { useFetcher } from '@remix-run/react'
import { AtSignIcon, TrashIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '~/helpers/form'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { patchShopSchema } from '~/schemas/shop'
import { Button, Dialog, Input, Alert } from '~/ui'
import { cn } from '~/utils/classnames'

type Props = {
  onClose: () => void
  open: boolean
}

export const SupportInstagramModal = ({ open, onClose }: Props) => {
  let { t } = useTranslation()
  const fetcher = useFetcher()
  const shop = useShop()

  const [removeIsLoading, setRemoveIsLoading] = useState(false)
  const submitIsLoading = useMemo(
    () => fetcher.state != 'idle' && !removeIsLoading,
    [fetcher, removeIsLoading]
  )

  useEffect(() => {
    if (open && fetcher.state === 'loading') {
      setRemoveIsLoading(false)
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, open])

  const onRemoveSupportInstagram = useCallback(() => {
    setRemoveIsLoading(true)
    fetcher.submit(
      { instagramHandle: '' },
      {
        method: 'PATCH',
        action: makeShopOwnPath(shop, '/settings?index'),
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher])

  return (
    <Dialog
      open={open}
      onOpenChange={(_open) => {
        if (!_open) onClose()
      }}
    >
      <Dialog.Content>
        <Dialog.Title>
          {t(`components.support_modal.instagram_support_title`)}
        </Dialog.Title>
        <Form
          mode="onBlur"
          className="flex w-full flex-col items-stretch p-3"
          action={makeShopOwnPath(shop, '/settings?index')}
          schema={patchShopSchema.pick({ instagramHandle: true })}
          method="PATCH"
          fetcher={fetcher}
          values={{
            instagramHandle: shop.instagramHandle,
          }}
        >
          {({
            Field,
            register,
            getFieldState,
            formState,
            clearErrors,
            getValues,
          }) => {
            const fieldState = getFieldState('instagramHandle')
            const fieldValue = getValues('instagramHandle')

            return (
              <>
                <p className="text-foreground/64 mb-5 max-w-[453px] text-xs font-medium">
                  {t(`components.support_modal.instagram_support_description`)}
                </p>

                <Field
                  name={'instagramHandle'}
                  className="flex flex-col items-stretch gap-2"
                >
                  {({ Label }) => {
                    return (
                      <>
                        <Label className="text-foreground/64 text-xs">
                          {t(
                            `components.support_modal.instagram_support_label`
                          )}
                        </Label>
                        <Input.Group>
                          <Input.Prefix>
                            <AtSignIcon
                              size={20}
                              className="text-foreground/64"
                            />
                          </Input.Prefix>

                          <Input
                            {...register('instagramHandle')}
                            placeholder={t(
                              `components.support_modal.instagram_support_placeholder`
                            )}
                            onInput={() => clearErrors()}
                            autoComplete="off"
                          />
                        </Input.Group>
                      </>
                    )
                  }}
                </Field>

                {fieldState.error?.message && fieldState.isDirty && (
                  <Alert
                    className="mt-3"
                    type="error"
                    title={t(fieldState.error?.message)!}
                  />
                )}

                <div className="mt-5 flex flex-col items-stretch gap-3 sm:flex-row sm:items-start">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!formState.isValid || !fieldValue?.length}
                    isLoading={submitIsLoading}
                  >
                    <p className={cn(submitIsLoading && 'invisible')}>
                      {shop.instagramHandle !== null
                        ? t('action.update')
                        : t('action.confirm')}
                    </p>
                  </Button>
                  {shop.instagramHandle !== null && (
                    <Button
                      type="button"
                      variant="ghost"
                      isLoading={removeIsLoading}
                      onClick={onRemoveSupportInstagram}
                    >
                      <TrashIcon />
                      {t(`action.remove_instagram`)}
                    </Button>
                  )}
                </div>
              </>
            )
          }}
        </Form>
      </Dialog.Content>
    </Dialog>
  )
}
